import { ShieldCheck, HelpCircle } from 'lucide-react';
import { cn } from '../ui/utils';
import {
  RISK_BAND_LABEL,
  RISK_BAND_MEANING,
  riskFactorLabel,
  type OrganScore,
  type OrganRiskBand,
} from './organHealth';

/**
 * The modifiable-risk track (rubric v0.10) — the second of an organ's two
 * parallel scores.
 *
 * DELIBERATELY UNLIKE THE FUNCTION BAND. The design system reserves the
 * `--band-*` severity hues for organ function scores, and this component honours
 * that for a clinical reason, not just a token rule: a function score of 25 and
 * a risk score of 25 are both alarming but mean opposite things and demand
 * different action — treat established disease vs. optimize modifiable factors.
 * Giving them the same red chip would invite a clinician to read one as the
 * other. So risk gets its own language: a neutral track with a brand-coloured
 * fill, no severity ramp anywhere.
 *
 * THE BAR FILLS TOWARD GOOD. Risk scores run higher = better care, the inverse
 * of every severity scale on this screen. A meter that fills as the patient is
 * better protected makes that polarity legible without anyone having to
 * remember it — a nearly-empty bar reads as "little protection here", which is
 * exactly what a risk score of 25 means.
 *
 * Severity is never colour-only: every state carries the band word and the
 * number, and the meter is exposed to assistive tech as a labelled meter role.
 */

function riskPercent(score: number | null | undefined): number {
  if (typeof score !== 'number') return 0;
  return Math.max(0, Math.min(100, score));
}

/** Compact one-line risk read: meter + band word + score. Used on the card. */
export function RiskMeter({
  score,
  band,
  className,
  showLabel = true,
}: {
  score: number | null | undefined;
  band: OrganRiskBand | undefined;
  className?: string;
  showLabel?: boolean;
}) {
  const resolved: OrganRiskBand = band ?? 'unknown';
  const scored = typeof score === 'number';
  const pct = riskPercent(score);

  return (
    <div className={cn('w-full', className)}>
      {showLabel && (
        <div className="flex items-baseline justify-between gap-2 text-xs">
          <span
            className="inline-flex items-center gap-1 text-text-tertiary"
            title="The organ's OTHER track: changeable risk factors for future disease (blood pressure, lipids, smoking, BMI, activity, family history) — not organ damage. Higher is better; 100 means nothing left to optimize. Scored independently of function, so it can exist while function cannot be graded."
          >
            <ShieldCheck className="size-3" aria-hidden="true" />
            Modifiable risk
            <span className="font-normal text-text-tertiary/70">· higher is better</span>
          </span>
          <span className={cn('font-medium tabular-nums', scored ? 'text-text-secondary' : 'text-text-disabled')}>
            {scored ? `${RISK_BAND_LABEL[resolved]} · ${score}` : RISK_BAND_LABEL.unknown}
          </span>
        </div>
      )}
      <div
        className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-track-off"
        role="meter"
        aria-valuenow={scored ? pct : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={
          scored
            ? `Modifiable risk score ${score} of 100, ${RISK_BAND_LABEL[resolved]}. Higher is better care.`
            : 'Modifiable risk not scored'
        }
      >
        {scored ? (
          <div className="h-full rounded-full bg-primary transition-[width] duration-200" style={{ width: `${pct}%` }} />
        ) : (
          // Not scored is NOT zero risk. A dashed empty track says "unmeasured",
          // where a 0%-filled solid bar would read as "no protection at all".
          <div className="h-full w-full border-t border-dashed border-border" aria-hidden="true" />
        )}
      </div>
    </div>
  );
}

/** One domain's earned/available points with its factor rows. */
function DomainBlock({
  domainKey,
  domain,
}: {
  domainKey: string;
  domain: NonNullable<
    NonNullable<NonNullable<OrganScore['structured_fields']>['risk_track']>['domains']
  >[string];
}) {
  const factors = Object.entries(domain.factors ?? {});
  const dropped = domain.declared_max - domain.available_max;

  return (
    <div className="rounded border border-border-subtle bg-surface-raised p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-[0.08em] text-text-tertiary">
          {domain.label}
        </span>
        <span className="text-xs font-medium tabular-nums text-text-secondary">
          {domain.earned} / {domain.available_max}
        </span>
      </div>

      {dropped > 0 && (
        // The denominator moved, and saying so is the point: a reader comparing
        // "3 / 7" against a documented maximum of 9 would otherwise assume a bug.
        <p className="mt-1 text-xs text-text-tertiary">
          {dropped} point{dropped === 1 ? '' : 's'} not scored — the factor{dropped === 1 ? ' was' : 's were'} not
          recorded, so {dropped === 1 ? 'it is' : 'they are'} dropped from the total rather than counted as zero.
        </p>
      )}

      <ul className="mt-2 space-y-1">
        {factors.map(([key, factor]) => (
          <li key={key} className="flex items-baseline justify-between gap-3 text-xs">
            <span className={cn(factor.missing ? 'text-text-disabled' : 'text-text-secondary')}>
              {riskFactorLabel(key)}
              {factor.band && !factor.missing && (
                <span className="ml-1.5 text-text-tertiary">{factor.band.replace(/_/g, ' ')}</span>
              )}
            </span>
            <span
              className={cn(
                'shrink-0 tabular-nums',
                factor.missing ? 'text-text-disabled' : 'font-medium text-text-primary',
              )}
            >
              {factor.missing ? 'not recorded' : `${factor.points} / ${factor.max}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Full risk-track panel for the detail view: the headline meter, what the band
 * means, the per-domain point breakdown, and what to collect.
 */
export function RiskTrackPanel({ organ }: { organ: OrganScore }) {
  const track = organ.structured_fields?.risk_track;
  const band: OrganRiskBand = organ.risk_band ?? 'unknown';
  const scored = typeof organ.risk_score === 'number';
  const penalty = organ.structured_fields?.risk_penalty;
  const suppressed = organ.structured_fields?.capped_by_red_flag === true;

  // A score withheld for thin coverage is NOT the same as nothing recorded, and
  // saying so matters: the clinician needs to know a number exists to be had and
  // what would unlock it, not conclude the track is broken or irrelevant.
  if (track?.insufficient_coverage) {
    const pct = typeof track.coverage === 'number' ? Math.round(track.coverage * 100) : null;
    return (
      <div className="rounded border border-dashed border-border p-4">
        <p className="inline-flex items-center gap-1.5 text-sm text-text-secondary">
          <HelpCircle className="size-4" aria-hidden="true" />
          Not enough recorded to score modifiable risk
          {pct !== null ? ` — ${pct}% of the factors are on file` : ''}.
        </p>
        <p className="mt-1 text-xs text-text-tertiary">
          Scoring the few that are recorded would read worse than this patient is: the factors
          typically missing are the protective ones, so a partial score is biased rather than
          merely uncertain.
        </p>
        {(track.missing_factors?.length ?? 0) > 0 && (
          <ul className="mt-2 space-y-0.5">
            {track.missing_factors!.map((key) => (
              <li key={key} className="text-xs text-text-secondary">
                {riskFactorLabel(key)}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (!track && !scored) {
    return (
      <div className="rounded border border-dashed border-border p-4">
        <p className="inline-flex items-center gap-1.5 text-sm text-text-tertiary">
          <HelpCircle className="size-4" aria-hidden="true" />
          No modifiable-risk factors on record for this organ.
        </p>
        <p className="mt-1 text-xs text-text-tertiary">
          An unmeasured risk profile is unknown, not low — it never lowers the score, only the confidence.
        </p>
      </div>
    );
  }

  const domains = Object.entries(track?.domains ?? {});
  const missing = track?.missing_factors ?? [];

  return (
    <div className="space-y-3">
      <div className="rounded border border-border-subtle bg-surface-raised p-4">
        <div className="flex items-end justify-between gap-4">
          <div className="flex items-baseline gap-1.5">
            <span
              className={cn(
                'text-2xl font-medium tabular-nums leading-none',
                scored ? 'text-text-primary' : 'text-text-disabled',
              )}
            >
              {scored ? organ.risk_score : '—'}
            </span>
            <span className="text-sm font-medium text-text-tertiary">/100</span>
          </div>
          <span className="text-sm font-medium text-text-secondary">{RISK_BAND_LABEL[band]}</span>
        </div>

        <RiskMeter score={organ.risk_score} band={band} showLabel={false} className="mt-3" />

        <p className="mt-2 text-xs text-text-tertiary">
          Higher is better care. {RISK_BAND_MEANING[band]}
        </p>

        {typeof penalty === 'number' && (
          <p className="mt-2 border-t border-border-subtle pt-2 text-xs text-text-secondary">
            {suppressed
              ? 'An acute red flag capped the function score, so no risk penalty was applied — the acute state governs.'
              : penalty > 0
                ? `Applied a ${penalty}-point downward adjustment to the headline score.`
                : 'No downward adjustment applied to the headline score.'}
          </p>
        )}
      </div>

      {domains.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {domains.map(([key, domain]) => (
            <DomainBlock key={key} domainKey={key} domain={domain} />
          ))}
        </div>
      )}

      {missing.length > 0 && (
        <div className="rounded border border-border-subtle p-3">
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-text-tertiary">
            Would sharpen this score
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {missing.map((key) => (
              <li key={key} className="text-xs text-text-secondary">
                {riskFactorLabel(key)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
